import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";

// Default sections
import { DefaultInput } from "@/PayPlans/helpers/DefaultSections";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { isSingleLastValid } from "@/Common/helpers/Matrices/tiers";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Layouts
import CheckInput from "@/Layouts/components/InputComponents/CheckInput";
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import LabeledInput from "@/Layouts/components/InputComponents/LabeledInput";
import RoundBoxDeletable from "@/Layouts/components/RoundBoxDeletable";
import TierItemsLayout from "@/Layouts/components/Tiers/TierItemsLayout";

// MUI
import { Stack } from "@mui/material";

const productCommission = (commissionInput = "Percent") => {
	return {
		from: DefaultInput(0, "Percent"),
		to: DefaultInput(100, "Percent"),
		commission: DefaultInput(0, commissionInput),
	};
};

const useFlatCommission = (data = []) => data.some((element) => element.commission.input !== "Percent");

export default function ProductItem(props) {
	const {
		index = 0,
		product = null,
		mainDeleteSx = null,
		defaultTiers = { rows: 1, columns: 1 },
		useDefaultRate = false,
		boostPVR = false,
		useGrossModifier = false,
		hasPVRDefinition = false,
		hasTotalGross = false,
		readOnly = false,
		onActionDelete = () => {},
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const lastCommissionError = Boolean(errors?.get("lastCommission")?.length);
	const localErrorsMessages = getErrorsMessages();

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onProductUpdate = (updatedProduct) => {
		onActionValidation?.(isValid);
		if (isValid && wasUpdated.current) {
			onActionUpdate?.(index, updatedProduct);
		}
	};

	// States
	const [innerProduct, setInnerProduct] = useCallbackState(null, onProductUpdate);
	const [switchDisabled, setSwitchDisabled] = useState(Boolean(innerProduct?.grossModifier?.value?.toString() === "0") || false);

	// Local hooks
	const isFlatCommissions = useFlatCommission(innerProduct?.tiers || []);

	// Methods
	const handleAddTier = () => {
		const actualTiers = [...innerProduct.tiers];
		const lastIndex = actualTiers?.length ? actualTiers.length - 1 : 1;
		const lastInput = actualTiers?.[lastIndex]?.["commission"]?.["input"] || "Percent";

		actualTiers.push(productCommission(lastInput));

		if (actualTiers?.length) {
			actualTiers[lastIndex].to.value = 0;
		}

		wasUpdated.current = false;
		setInnerProduct({ ...innerProduct, tiers: actualTiers });
	};

	const handleDeleteTier = () => {
		const actualTiers = [...innerProduct.tiers];
		if (actualTiers.length > 1) {
			actualTiers.splice(-1);
		}
		const lastIndex = actualTiers?.length ? actualTiers.length - 1 : 1;
		actualTiers[lastIndex].to.value = 100;
		updateProduct({ ...innerProduct, tiers: actualTiers });
	};

	const handleUpdateValue = (row, area, value) => {
		const actualTiers = [...innerProduct.tiers];
		actualTiers[row][area]["value"] = value ? Number(value) : 0;
		if (area === "to" && Boolean(actualTiers?.[row + 1])) {
			actualTiers[row + 1]["from"]["value"] = value ? Number(value) + 0.01 : 0;
		}
		updateProduct({ ...innerProduct, tiers: actualTiers });
	};

	const handleUpdateInput = (area, value) => {
		const updatedProduct = { ...innerProduct };
		const actualTiers = [...updatedProduct.tiers];
		actualTiers?.forEach((tier) => {
			tier[area]["input"] = value;
			tier[area]["value"] = 0;
		});

		if (useGrossModifier) {
			updatedProduct["boostPVR"] = false;
			updatedProduct["useGrossProfit"] = false;
			updatedProduct["grossModifier"]["value"] = 0;
			setSwitchDisabled(true);
		}

		updateProduct({ ...innerProduct, tiers: actualTiers });
	};

	const handleUpdateGrossModifier = (value = 0) => {
		const updatedProduct = { ...innerProduct };
		updatedProduct["grossModifier"]["value"] = value;

		if (useGrossModifier) {
			const isBoostDisabled = !value;
			if (isBoostDisabled) {
				updatedProduct["boostPVR"] = false;
			}
			setSwitchDisabled(isBoostDisabled);
		}

		updateProduct(updatedProduct);
	};

	const handleMinPVRPenetration = (area, value = 0) => {
		const updatedProduct = { ...innerProduct };
		if (hasPVRDefinition) {
			updatedProduct[area]["value"] = Number(value);
		}
		updateProduct(updatedProduct);
	};

	const handleUpdateSwitches = (area, value) => {
		const update = { ...innerProduct };
		update[area] = value;

		if (area === "useDefaultRate" && value) {
			const tiers = [...update.tiers];
			const tiersLength = update?.tiers?.length || 0;

			if (tiersLength > 1) {
				tiers.length = 2;
			} else {
				tiers.push(productCommission());
				tiers[0].to.value = 0;
			}

			tiers?.forEach((tier) => {
				tier["commission"] = DefaultInput(0, "Percent");
			});

			tiers[1]["to"]["value"] = 100;
			update["tiers"] = tiers;
		}

		updateProduct(update);
	};

	const updateProduct = (updatedProduct) => {
		wasUpdated.current = true;
		setInnerProduct(updatedProduct);
	};

	useMemo(() => {
		if (innerProduct?.tiers?.length) {
			validate([
				{
					definition: {
						useKey: "conversion",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Percent: 0.01 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerProduct.tiers || [],
				},
				{
					definition: {
						useKey: "commission",
						operators: {
							conditions: [
								{
									type: "SELF",
									on: "value",
									distinct: 0,
								},
								{
									type: "STEP",
									on: "value",
									compare: "<",
									deltas: { Dollar: 0 },
								},
							],
						},
						errorsMap: ["commission"],
						message: `Incorrect commissions definition`,
						validationType: "SIMPLE",
						specialProperties: {
							noFirstError: true,
							noLastError: Boolean(innerProduct?.useDefaultRate),
						},
					},
					section: innerProduct.tiers || [],
				},
				{
					definition: {
						useKey: "lastCommission",
						validationType: "CUSTOM",
						errorsMap: ["commissions"],
						message: "Last commission must be configured",
						operators: {
							customFn: (params = null) => {
								const { tiers = [], usingDefault = false } = params || {};
								const lastValid = isSingleLastValid(tiers);
								return [{ commission: !lastValid && !usingDefault }];
							},
							params: {
								usingDefault: Boolean(innerProduct?.useDefaultRate),
								tiers: innerProduct.tiers || [],
							},
						},
					},
					section: [{ commission: false }],
				},
			]);
		}
	}, [innerProduct, validate]);

	useEffect(() => {
		setInnerProduct(clone(product));
		setSwitchDisabled(Boolean(product?.grossModifier?.value?.toString() === "0") || false);
		return () => {
			setInnerProduct(null);
			wasUpdated.current = false;
		};
	}, [product, setInnerProduct]);

	const isDisabledByDefaultRate = useDefaultRate && innerProduct?.useDefaultRate;
	const isCommissionPercent = innerProduct?.tiers?.[0]?.commission?.input === "Percent";

	return (
		<RoundBoxDeletable
			title={innerProduct?.productName || "Undefined"}
			titleProps={{ className: "tiered-title", textOverflow: "ellipsis", overflow: "hidden" }}
			deletable
			deleteDisabled={readOnly}
			containerProps={{
				sx: { minWidth: "464px", width: "464px" },
			}}
			additionalComponent={
				<Stack mr={1} height={"40px"} spacing={0.5}>
					{Boolean(useGrossModifier) && (
						<Stack>
							<CheckInput
								checkbox={{
									label: "Gross Profit",
									checked: Boolean(innerProduct?.useGrossProfit),
									handleChange: (value) => handleUpdateSwitches("useGrossProfit", value),
								}}
								input={{
									readOnly,
									data: [
										{
											type: "Dollar",
											min: -1000000,
											max: 1000000,
											decimalsEditable: false,
											decimals: 2,
											dashed: isFlatCommissions,
										},
									],
									value: innerProduct?.grossModifier?.value || 0,
									defaultType: innerProduct?.grossModifier?.input || "Dollar",
									error: false,
									disabled: isFlatCommissions,
									sx: { maxWidth: "95px", minWidth: "95px", marginLeft: 1 },
									onActionUpdate: (value) => {
										handleUpdateGrossModifier(value);
									},
								}}
								disabled={isFlatCommissions}
								readOnly={readOnly}
							/>
						</Stack>
					)}
				</Stack>
			}
			confirmationDeleteSx={{ right: "-2px !important" }}
			mainDeleteSx={mainDeleteSx}
			onDeleteAction={() => onActionDelete?.(index)}
		>
			<Stack>
				{hasPVRDefinition && (
					<Stack my={2} direction={"row"} justifyContent={"space-between"} alignItems={"center"} alignContent={"center"}>
						<Stack>
							<LabeledInput
								label={"Min. PVR"}
								input={{
									readOnly,
									data: [
										{
											type: "Dollar",
											min: -1000000,
											max: 1000000,
											decimalsEditable: true,
											decimals: 2,
										},
									],
									value: innerProduct?.minPVR?.value || 0,
									defaultType: "Dollar",
									error: false,
									sx: { maxWidth: "95px", minWidth: "95px", marginLeft: 2 },
									onActionUpdate: (value) => handleMinPVRPenetration("minPVR", value),
								}}
								disabled={defaultTiers?.rows <= 1}
								dividerStyle={{ right: "-16px" }}
								divider={true}
								readOnly={readOnly}
							/>
						</Stack>
						<Stack>
							<LabeledInput
								label={"Min. Prod. Pen."}
								input={{
									readOnly,
									data: [
										{
											type: "Unit",
											min: 0,
											max: 100000,
											decimalsEditable: true,
											decimals: 2,
										},
									],
									value: innerProduct?.minPenetration?.value || 0,
									defaultType: "Unit",
									error: false,
									sx: { maxWidth: "60px", minWidth: "60px", marginLeft: 2 },
									onActionUpdate: (value) => handleMinPVRPenetration("minPenetration", value),
								}}
								disabled={defaultTiers?.columns <= 1}
								readOnly={readOnly}
							/>
						</Stack>
					</Stack>
				)}
				{Boolean(useDefaultRate || boostPVR) && (
					<Stack mt={1.5} direction={"row"} sx={{ justifyContent: "flex-end", width: "341px" }}>
						{Boolean(boostPVR) && (
							<CustomizedSwitches
								textLeft
								textChecked={"Boost PVR"}
								textUnChecked={"Boost PVR"}
								checked={Boolean(innerProduct?.boostPVR)}
								onChange={(value) => handleUpdateSwitches("boostPVR", value)}
								disabled={switchDisabled || readOnly}
							/>
						)}
						{Boolean(useDefaultRate) && (
							<CustomizedSwitches
								textLeft
								textChecked={"Use default rate"}
								textUnChecked={"Use default rate"}
								checked={Boolean(innerProduct?.useDefaultRate)}
								onChange={(value) => handleUpdateSwitches("useDefaultRate", value)}
								disabled={readOnly}
							/>
						)}
					</Stack>
				)}
			</Stack>
			<Stack mt={useGrossModifier || useDefaultRate ? 1 : 0} width={"350px"}>
				<TierItemsLayout
					minimumRows={1}
					columnSpacing={9}
					rows={innerProduct?.tiers || []}
					columns={[
						{
							_id: "conversion",
							title: "Conversion",
							component: {
								type: "DoubleInput",
								order: ["from", "to"],
								disableCommonInputs: true,
								properties: {
									firstInput: {
										data: [
											{
												type: "Percent",
												min: 0,
												max: 100,
												decimalsEditable: true,
												decimals: 2,
												disabled: true,
											},
										],
										sx: { maxWidth: "75px" },
									},
									secondInput: {
										data: [
											{
												type: "Percent",
												min: 0,
												max: 100,
												decimalsEditable: true,
												decimals: 2,
											},
										],
										sx: { maxWidth: "75px" },
									},
								},
								callbacks: {
									onActionUpdate: (positions, area, value) => handleUpdateValue(positions.row, area, value),
								},
							},
						},
						{
							_id: "commission",
							title: "Comm.",
							component: {
								type: "InputForm",
								order: ["commission"],
								properties: {
									input: {
										data: [
											{
												type: "Percent",
												min: 0,
												max: 100,
												decimalsEditable: true,
												decimals: 2,
												disabled: isDisabledByDefaultRate,
												defaultValue: isDisabledByDefaultRate,
											},
											{
												type: "Dollar",
												min: 0,
												max: 10000,
												decimalsEditable: false,
												decimals: 2,
												disabled: isDisabledByDefaultRate,
											},
											{
												type: "Dollar per Unit",
												min: 0,
												max: 10000,
												decimalsEditable: false,
												decimals: 2,
												disabled: isDisabledByDefaultRate,
											},
										],
										sx: { maxWidth: "95px" },
									},
								},
								callbacks: {
									onActionUpdate: (positions, area, value) => handleUpdateValue(positions.row, area, value),
									onChangeType: (_positions, area, _value, type) => handleUpdateInput(area, type),
								},
							},
						},
					]}
					errors={errors}
					lastCommissionError={lastCommissionError}
					errorsMessages={localErrorsMessages}
					addButtonDisabled={isDisabledByDefaultRate}
					deleteButtonDisabled={isDisabledByDefaultRate}
					readOnly={readOnly}
					onActionAdd={() => handleAddTier()}
					onActionDelete={() => handleDeleteTier()}
				/>
			</Stack>
			<Stack mb={2} justifyContent={"center"} alignItems={"flex-end"}>
				{hasTotalGross && (
					<Stack mt={2}>
						<CustomizedSwitches
							key={"applyTotalGross"}
							textLeft={true}
							textChecked={"Total Gross"}
							textUnChecked={"Product Gross"}
							checked={Boolean(innerProduct?.applyTotalGross)}
							disabled={readOnly || !isCommissionPercent}
							onChange={(value) => handleUpdateSwitches("applyTotalGross", value)}
							tooltip={{
								title: "Apply over Total or Product Gross",
								placement: "bottom",
								key: "default-commissions-guarantee-draw-tooltip",
							}}
						/>
					</Stack>
				)}
			</Stack>
		</RoundBoxDeletable>
	);
}
ProductItem.propTypes = {
	index: PropTypes.number,
	product: PropTypes.object,
	mainDeleteSx: PropTypes.object,
	defaultTiers: PropTypes.shape({
		rows: PropTypes.number,
		columns: PropTypes.number,
	}),
	useDefaultRate: PropTypes.bool,
	boostPVR: PropTypes.bool,
	useGrossModifier: PropTypes.bool,
	hasPVRDefinition: PropTypes.bool,
	hasTotalGross: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionDelete: PropTypes.func,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
